/**
 * ReconstructionPanel -- read-only progress view of the point-in-time parameter reconstruction (Testing page, T-211).
 *
 * Binds to GET /api/testing/reconstruction (shared/reconstruction_status.py). It shows run health (running / stalled / complete / stopped), how far
 * the window is covered, the per-epoch funnel and the snapshot mix (tier and spread source). No performance figure is shown: this run produces
 * parameters, not trades. Polls every 30 s while a run is healthy-running and stops polling otherwise.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import { reconstructionStatus } from '../lib/api'
import { HEALTH_STYLE, fmtPct, fmtTs, anyRunning, progressPct, symbolCount } from '../lib/reconstruction'

const mono = { fontFamily: 'JetBrains Mono, monospace' }
const card = { background: '#111827', border: '1px solid #1f2937', borderRadius: '0.75rem' }
const POLL_MS = 30000

function Kv({ label, value }) {
  return (
    <div className="rounded-lg p-3" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-slate-200 mt-1" style={mono}>{value}</p>
    </div>
  )
}

function RunCard({ run }) {
  const h = HEALTH_STYLE[run.health] || { label: String(run.health || 'UNKNOWN').toUpperCase(), cls: 'bg-slate-800 text-slate-400 border-slate-700' }
  const prog = progressPct(run)
  const sn = run.snapshots
  return (
    <div className="p-4 space-y-3" style={card} data-testid={`run-${run.run_name}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.04em' }}>{run.run_name}</p>
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${h.cls}`}>{h.label}</span>
      </div>
      <div>
        <div className="h-2 rounded bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prog ?? 0}
             aria-label={`${run.run_name} calendar progress`}>
          <div className="h-full bg-cyan-500" style={{ width: `${prog ?? 0}%` }} />
        </div>
        <p className="text-[11px] text-slate-500 mt-1" style={mono}>
          {prog == null ? 'progress n/a' : `${prog} % of the calendar window (approximate; sessions only)`} · {run.epochs_done.toLocaleString()} epochs committed
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kv label="Window" value={`${String(run.window.start || '').slice(0, 10)} .. ${String(run.window.end || '').slice(0, 10)}`} />
        <Kv label="Last epoch" value={fmtTs(run.last_epoch)} />
        <Kv label="Last commit" value={fmtTs(run.updated_at)} />
        <Kv label="Symbols" value={symbolCount(run.config)} />
        <Kv label="Avg universe / fitted per epoch" value={`${run.funnel.avg_universe == null ? 'n/a' : run.funnel.avg_universe.toFixed(0)} / ${run.funnel.avg_fitted == null ? 'n/a' : run.funnel.avg_fitted.toFixed(1)}`} />
        <Kv label="Snapshot rows" value={`${sn.rows.toLocaleString()} (${sn.tickers} tickers)`} />
        <Kv label="Tier A / production" value={`${fmtPct(sn.tier_a_pct)} / ${fmtPct(sn.tier_production_pct)}`} />
        <Kv label="Spread symbol / global / fallback" value={`${fmtPct(sn.spread_symbol_pct, 0)} / ${fmtPct(sn.spread_global_pct, 0)} / ${fmtPct(sn.spread_fallback_pct, 0)}`} />
      </div>
      {run.health === 'stalled' && (
        <div className="flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)' }}>
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-xs text-red-200">Marked running but the lease expired or the last commit is older than the staleness limit. Re-run the same command to resume from the checkpoint.</p>
        </div>
      )}
    </div>
  )
}

export default function ReconstructionPanel() {
  const [resp, setResp] = useState(undefined)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      const r = await reconstructionStatus.get()
      if (!r.success) throw new Error(r.error || 'failed')
      setResp(r)
    } catch (e) {
      setErr(e?.message || 'failed to load')
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!anyRunning(resp)) return undefined
    const id = setInterval(load, POLL_MS)
    return () => clearInterval(id)
  }, [resp, load])

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.4)' }}>
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-200">Reconstructed parameters only; no trades and no P&amp;L. Fits inherit the stored indicator gaps and mostly a global or fallback spread. Relative evidence.</p>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-widest" style={{ fontFamily: 'Rajdhani, sans-serif' }}>POINT-IN-TIME PARAMETER RECONSTRUCTION</h3>
        <button onClick={load} className="text-slate-400 hover:text-slate-200" aria-label="Refresh reconstruction status"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {err && <p className="text-xs text-red-400">Could not load the status: {err}</p>}
      {resp === undefined && !err && <p className="text-xs text-slate-500">Loading...</p>}
      {resp && resp.tables_present === false && <p className="text-xs text-slate-500">The reconstruction tables do not exist yet. Run src/backtest/reconstruct_params.py to create them.</p>}
      {resp && resp.tables_present !== false && resp.runs.length === 0 && <p className="text-xs text-slate-500">No run recorded yet.</p>}
      {resp && resp.runs.map((run) => <RunCard key={run.run_name} run={run} />)}
      {resp && <p className="text-[11px] text-slate-600" style={mono}>generated {fmtTs(resp.generated_at)}{anyRunning(resp) ? ' · refreshing every 30 s' : ''}</p>}
    </div>
  )
}
